import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  Avatar,
} from '@mui/material';
import {
  LocalDining,
  Science,
  Thermostat,
  WaterDrop,
  CheckCircle,
  Warning,
  Error,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface MilkQualityReportProps {
  batchId?: string;
}

export const MilkQualityReport: React.FC<MilkQualityReportProps> = ({ batchId = 'MLK-2024-0342' }) => {
  // Mock data for dairy operations
  const milkData = {
    batchId,
    collectionDate: '28/11/2024',
    totalVolume: '15,200 litros',
    producerCount: 23,
    averageQuality: 'A',
    temperature: '4.2°C',
    qualityScore: 92.5,
    status: 'Aprovado',
    technician: 'Ana Oliveira',
    qualityTests: [
      { test: 'Gordura', value: '3.8%', status: 'normal', target: '3.0-4.5%' },
      { test: 'Proteína', value: '3.2%', status: 'normal', target: '2.8-3.8%' },
      { test: 'Lactose', value: '4.7%', status: 'normal', target: '4.0-5.2%' },
      { test: 'Sólidos Totais', value: '12.1%', status: 'good', target: '11.5-13.0%' },
      { test: 'CCS (x1000)', value: '185', status: 'normal', target: '<400' },
      { test: 'CBT (x1000)', value: '12', status: 'good', target: '<100' },
    ],
    producers: [
      { name: 'Fazenda Vista Alegre', volume: '3,200L', quality: 'A+', score: 96 },
      { name: 'Sítio Boa Esperança', volume: '2,800L', quality: 'A', score: 91 },
      { name: 'Rancho do Vale', volume: '2,100L', quality: 'A', score: 89 },
      { name: 'Fazenda Primavera', volume: '1,900L', quality: 'B+', score: 85 },
      { name: 'Outros (19 produtores)', volume: '5,200L', quality: 'A-', score: 88 },
    ],
    physicalChemical: [
      { parameter: 'Densidade', value: '1.031', status: 'normal', unit: 'g/cm³' },
      { parameter: 'Acidez', value: '16°D', status: 'normal', unit: '°Dornic' },
      { parameter: 'pH', value: '6.7', status: 'normal', unit: '' },
      { parameter: 'Crioscopia', value: '-0.531°H', status: 'normal', unit: '°Hortvet' },
    ],
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return '#6BC43C';
      case 'good': return '#4ECDC4';
      case 'warning': return '#F59E0B';
      case 'error': return '#EF4444';
      default: return '#94A3B8';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'normal': return <CheckCircle />;
      case 'good': return <CheckCircle />;
      case 'warning': return <Warning />;
      case 'error': return <Error />;
      default: return <Science />;
    }
  };

  const getQualityColor = (quality: string) => {
    if (quality.startsWith('A')) return '#4ECDC4';
    if (quality.startsWith('B')) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: '#4ECDC4',
            mx: 'auto',
            mb: 2,
            fontSize: '2rem',
          }}
        >
          <LocalDining sx={{ fontSize: '2.5rem' }} />
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.dairy }}>
          Relatório de Qualidade do Leite
        </Typography>
        <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
          Lote {milkData.batchId}
        </Typography>
        <Chip 
          label={milkData.status} 
          color="success" 
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Collection Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.dairy }}>
            Resumo da Coleta
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WaterDrop sx={{ color: DESIGN_TOKENS.colors.organization.dairy }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Volume Total</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{milkData.totalVolume}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocalDining sx={{ color: DESIGN_TOKENS.colors.organization.dairy }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Produtores</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{milkData.producerCount}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Thermostat sx={{ color: DESIGN_TOKENS.colors.organization.dairy }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Temperatura</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{milkData.temperature}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Science sx={{ color: DESIGN_TOKENS.colors.organization.dairy }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Qualidade Média</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{milkData.averageQuality}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Quality Score */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.dairy }}>
            Pontuação de Qualidade
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Score Geral: {milkData.qualityScore}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={milkData.qualityScore} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#E2E8F0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: milkData.qualityScore >= 90 ? '#4ECDC4' : milkData.qualityScore >= 80 ? '#F59E0B' : '#EF4444',
                }
              }} 
            />
          </Box>
        </CardContent>
      </Card>

      {/* Quality Tests */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.dairy }}>
            Análises Laboratoriais
          </Typography>
          {milkData.qualityTests.map((test, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              py: 2,
              borderBottom: index < milkData.qualityTests.length - 1 ? '1px solid #E2E8F0' : 'none'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: getStatusColor(test.status) }}>
                  {getStatusIcon(test.status)}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {test.test}
                  </Typography>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    Referência: {test.target}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ 
                fontWeight: 600, 
                color: getStatusColor(test.status) 
              }}>
                {test.value}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Physical-Chemical Parameters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.dairy }}>
            Parâmetros Físico-Químicos
          </Typography>
          {milkData.physicalChemical.map((param, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              py: 1.5,
              borderBottom: index < milkData.physicalChemical.length - 1 ? '1px solid #E2E8F0' : 'none'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {param.parameter}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {param.value} {param.unit}
                </Typography>
                <Box sx={{ color: getStatusColor(param.status) }}>
                  {getStatusIcon(param.status)}
                </Box>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Top Producers */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.organization.dairy }}>
            Principais Produtores
          </Typography>
          {milkData.producers.map((producer, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              py: 2,
              borderBottom: index < milkData.producers.length - 1 ? '1px solid #E2E8F0' : 'none'
            }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {producer.name}
                </Typography>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  {producer.volume}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={producer.quality}
                  size="small"
                  sx={{
                    bgcolor: `${getQualityColor(producer.quality)}20`,
                    color: getQualityColor(producer.quality),
                    fontWeight: 600,
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.dairy }}>
                  {producer.score}%
                </Typography>
              </Box>
            </Box>
          ))}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E2E8F0' }}>
            <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              Técnico responsável: {milkData.technician}
            </Typography>
            <br />
            <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              Data da coleta: {milkData.collectionDate}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};